import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Calendar,
  Plus,
  CheckCircle2,
  AlertCircle,
  Clock,
  X,
  Check,
  ShieldAlert
} from 'lucide-react';

interface AcademicSession {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  is_active: number;
  created_at: string;
}

export const AcademicSessionsPage: React.FC = () => {
  const { fetchWithAuth, refreshMe } = useAuth();
  const { t } = useLanguage();

  const [sessions, setSessions] = useState<AcademicSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSession, setNewSession] = useState({ name: '', start_date: '', end_date: '' });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/academic-sessions');
      const data = await res.json();
      if (data.success) {
        setSessions(data.sessions);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSession.name.trim()) return;

    setSaving(true);
    try {
      const res = await fetchWithAuth('/api/academic-sessions', {
        method: 'POST',
        body: JSON.stringify(newSession)
      });
      const data = await res.json();
      if (data.success) {
        setShowAddModal(false);
        setNewSession({ name: '', start_date: '', end_date: '' });
        setMessage({ type: 'success', text: data.message });
        loadSessions();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to create session.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server connection error.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (session: AcademicSession) => {
    if (session.is_active) return;
    setSaving(true);
    try {
      const res = await fetchWithAuth(`/api/academic-sessions/${session.id}/set-active`, {
        method: 'PUT'
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        await loadSessions();
        await refreshMe();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update active session.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-[#0F2C59] text-white rounded-xl shadow-xs">
            <Calendar className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">
              {t('nav_academic_sessions')}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              School academic year calendar management and active timeline selection
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="bg-[#0F2C59] hover:bg-[#081B38] text-white text-xs font-bold py-2.5 px-4 rounded-lg shadow-sm flex items-center justify-center space-x-2 transition"
        >
          <Plus className="w-4 h-4 text-amber-400" />
          <span>{t('createSession')}</span>
        </button>
      </div>

      {message && (
        <div
          className={`p-4 rounded-xl text-xs flex items-center justify-between border ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
              : 'bg-rose-50 text-rose-800 border-rose-300'
          }`}
        >
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span className="font-semibold">{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Info Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900 flex items-start space-x-2.5">
        <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <span className="font-bold">Government School Calendar Policy: </span>
          {t('activeSessionWarning')}
        </div>
      </div>

      {/* Sessions List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-[#0F2C59] text-white uppercase tracking-wider text-[11px] font-semibold">
              <th className="py-3 px-4">Session Name</th>
              <th className="py-3 px-4">Start Date</th>
              <th className="py-3 px-4">End Date</th>
              <th className="py-3 px-4 text-center">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {sessions.map((session) => (
              <tr key={session.id} className={session.is_active ? 'bg-amber-50/40 font-medium' : 'hover:bg-slate-50'}>
                <td className="py-3.5 px-4 font-mono font-bold text-slate-900 text-sm">
                  {session.name}
                </td>
                <td className="py-3.5 px-4 text-slate-600">
                  {session.start_date || '01-Apr'}
                </td>
                <td className="py-3.5 px-4 text-slate-600">
                  {session.end_date || '31-Mar'}
                </td>
                <td className="py-3.5 px-4 text-center">
                  {session.is_active ? (
                    <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-bold px-2.5 py-1 rounded-full inline-flex items-center space-x-1">
                      <Check className="w-3 h-3" />
                      <span>CURRENT ACTIVE</span>
                    </span>
                  ) : (
                    <span className="bg-slate-100 text-slate-600 text-[10px] font-medium px-2 py-0.5 rounded">
                      Archived / Inactive
                    </span>
                  )}
                </td>
                <td className="py-3.5 px-4 text-right">
                  {!session.is_active && (
                    <button
                      onClick={() => handleSetActive(session)}
                      disabled={saving}
                      className="bg-[#0F2C59] hover:bg-[#081B38] text-white text-[11px] font-bold py-1.5 px-3 rounded shadow-xs transition"
                    >
                      {t('setActiveSession')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Session Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-slate-300 overflow-hidden">
            <div className="bg-[#0F2C59] text-white px-5 py-4 flex justify-between items-center border-b-2 border-amber-500">
              <h3 className="text-sm font-bold">{t('createSession')}</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-300 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateSession} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  {t('sessionName')} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newSession.name}
                  onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
                  placeholder="e.g. 2026-2027"
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  {t('startDate')}
                </label>
                <input
                  type="date"
                  value={newSession.start_date}
                  onChange={(e) => setNewSession({ ...newSession, start_date: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  {t('endDate')}
                </label>
                <input
                  type="date"
                  value={newSession.end_date}
                  onChange={(e) => setNewSession({ ...newSession, end_date: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50"
                />
              </div>

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="bg-slate-200 text-slate-700 text-xs font-bold py-2 px-4 rounded-lg"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-[#0F2C59] text-white text-xs font-bold py-2 px-5 rounded-lg shadow-sm"
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
